import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MENU_HOST, isActionClick, menuItems, moveMenuFocus } from './menu-keyboard';

@Component({
  selector: 'app-content-dropdown-submenu-cabinet',
  templateUrl: './content-dropdown-submenu.component.html',
  styleUrls: ['./content-dropdown-submenu.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ContentDropdownSubmenuCabinetComponent {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);
  private readonly menuHost = inject(MENU_HOST, { optional: true, skipSelf: true });

  label = input.required<string>();
  itemSelected = output<void>();

  triggerEl = viewChild.required<ElementRef<HTMLElement>>('triggerEl');
  submenuTemplate = viewChild.required<TemplateRef<unknown>>('submenuTemplate');

  private overlayRef: OverlayRef | null = null;
  isOpen = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly OPEN_DELAY = 150;
  private readonly CLOSE_DELAY = 200;

  private readonly restoreBeforeAction = (event: Event): void => {
    if (isActionClick(event)) this.menuHost?.restoreTriggerFocus();
  };

  private positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 0, offsetY: -4 },
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: 0, offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 0, offsetY: 4 },
    { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: 0, offsetY: 4 },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => this.dispose());
  }

  onTriggerEnter(): void {
    this.cancelClose();
    if (!this.isOpen) {
      this.openTimer = setTimeout(() => this.open(), this.OPEN_DELAY);
    }
  }

  onTriggerLeave(): void {
    this.cancelOpen();
    this.scheduleClose();
  }

  onPanelEnter(): void {
    this.cancelClose();
  }

  onPanelLeave(): void {
    this.scheduleClose();
  }

  open(): void {
    if (this.isOpen) return;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.triggerEl().nativeElement)
        .withPositions(this.positions)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withViewportMargin(10);

      this.overlayRef = this.overlay.create({ positionStrategy });
      // Capture: focus goes back to the menu's trigger before the item's action runs.
      this.overlayRef.overlayElement.addEventListener('click', this.restoreBeforeAction, true);
    }

    const portal = new TemplatePortal(this.submenuTemplate(), this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isOpen = true;
  }

  /** Opens the submenu and moves focus to its first item. */
  openFromKeyboard(): void {
    this.cancelClose();
    this.open();
    const panel = this.overlayRef?.overlayElement.querySelector<HTMLElement>('[role="menu"]');
    (menuItems(panel)[0] ?? panel)?.focus({ preventScroll: true });
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.openFromKeyboard();
    }
  }

  /** Arrow keys move inside the submenu; ArrowLeft or Escape closes it and returns to its trigger. */
  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.triggerEl().nativeElement.focus();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      this.close();
      this.triggerEl().nativeElement.focus();
      return;
    }
    if (moveMenuFocus(event.currentTarget as HTMLElement, event.key)) {
      event.preventDefault();
    }
  }

  /** Whether focus is inside this submenu's panel. */
  containsFocus(): boolean {
    const active = document.activeElement;
    return !!active && !!this.overlayRef?.overlayElement.contains(active);
  }

  onPanelClick(): void {
    this.close();
    this.itemSelected.emit();
  }

  close(): void {
    this.cancelOpen();
    this.cancelClose();
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.isOpen = false;
  }

  private scheduleClose(): void {
    this.closeTimer = setTimeout(() => this.close(), this.CLOSE_DELAY);
  }

  private cancelOpen(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private dispose(): void {
    this.cancelOpen();
    this.cancelClose();
    if (this.overlayRef) {
      this.overlayRef.overlayElement?.removeEventListener('click', this.restoreBeforeAction, true);
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
